'use client';

import { useEffect, useRef } from 'react';

import Icon from '@/components/ui/AppIcon';

const archImages = [
{ src: "https://img.rocket.new/generatedImages/rocket_gen_img_14f975279-1768180982423.png", alt: 'Maison de vacances en Provence avec piscine et jardin verdoyant', style: { left: '3%', top: '72%', transform: 'rotate(-36deg)' } },
{ src: "https://images.unsplash.com/photo-1733254728953-796f2c27f468", alt: 'Gîte de charme avec vue sur la campagne française', style: { left: '13%', top: '42%', transform: 'rotate(-24deg)' } },
{ src: "https://img.rocket.new/generatedImages/rocket_gen_img_18c6d33c5-1769432645647.png", alt: 'Appartement de standing avec terrasse en bord de mer', style: { left: '24%', top: '16%', transform: 'rotate(-14deg)' } },
{ src: "https://images.unsplash.com/photo-1611444756156-bbe40e0a2e56", alt: 'Chambre moderne dans un gîte de luxe', style: { left: '37%', top: '5%', transform: 'rotate(-4deg)' } },
{ src: "https://img.rocket.new/generatedImages/rocket_gen_img_1212dacbb-1770501058527.png", alt: 'Villa avec piscine privée et vue panoramique', style: { left: '51%', top: '5%', transform: 'rotate(5deg)' } },
{ src: "https://img.rocket.new/generatedImages/rocket_gen_img_1ef616c05-1768017646640.png", alt: 'Maison contemporaine avec jardin aménagé', style: { left: '64%', top: '16%', transform: 'rotate(15deg)' } },
{ src: "https://img.rocket.new/generatedImages/rocket_gen_img_1104de789-1767388028169.png", alt: 'Résidence de luxe avec vue sur la mer Méditerranée', style: { left: '76%', top: '42%', transform: 'rotate(25deg)' } },
{ src: "https://img.rocket.new/generatedImages/rocket_gen_img_106840d57-1770255899650.png", alt: 'Chalet alpin confortable avec vue sur les montagnes', style: { left: '85%', top: '72%', transform: 'rotate(36deg)' } }];


export default function HeroSection() {
  const archRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const imgs = archRef.current?.querySelectorAll('.arch-image');
    if (!imgs) return;

    let delay = 200;
    imgs.forEach((img) => {
      setTimeout(() => {
        (img as HTMLElement).style.opacity = '1';
        (img as HTMLElement).style.transform = (img as HTMLElement).dataset.transform || '';
      }, delay);
      delay += 120;
    });
  }, []);

  return (
    <section className="relative min-h-screen flex flex-col items-center justify-center pt-44 pb-24 overflow-hidden">
      {/* Arch gallery */}
      <div
        ref={archRef}
        className="absolute top-28 left-1/2 -translate-x-1/2 w-full max-w-[1200px] h-[380px] pointer-events-none">
        
        {archImages.map((img, i) =>
        <img
          key={i}
          src={img.src}
          alt={img.alt}
          className="arch-image pointer-events-auto"
          style={{
            ...img.style,
            opacity: 0,
            transition: `opacity 0.8s ease ${i * 0.1}s, transform 0.5s cubic-bezier(0.2, 0.8, 0.2, 1)`
          }}
          data-transform={img.style.transform} />

        )}
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 relative z-10 text-center mt-44">
        {/* Badge */}
        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-moss/40 bg-moss/10 text-xs font-semibold tracking-widest text-moss-light uppercase mb-8 badge-pulse reveal-el revealed delay-1">
          <span className="w-1.5 h-1.5 rounded-full bg-moss-light" />
          Programme Partenaire 2026 — Places Limitées
        </div>

        <h1 className="font-fraunces text-5xl md:text-6xl lg:text-7xl font-light italic leading-[1.05] mb-6 text-cream reveal-el revealed delay-2">
          Programme Partenaire<br />
          <span className="text-moss-light not-italic font-fraunces">Conciergerie GiteZen</span>
        </h1>

        <p className="text-lg md:text-xl text-cream/60 max-w-2xl mx-auto leading-relaxed mb-10 reveal-el revealed delay-3">
          Accédez à <strong className="text-cream font-semibold">3 mois premium offerts</strong>, bénéficiez d'une mise en avant stratégique et participez à l'évolution d'un outil conçu pour les gestionnaires multi-biens.
        </p>

        <div className="flex flex-col sm:flex-row gap-4 justify-center reveal-el revealed delay-4">
          <a
            href="/candidature"
            className="group inline-flex items-center justify-center gap-3 bg-moss text-cream px-10 py-4 rounded-full text-base font-bold hover:bg-moss-light transition-all duration-300 shadow-xl shadow-moss/25">
            
            Candidater au Programme
            <Icon name="ArrowRightIcon" size={18} className="group-hover:translate-x-1 transition-transform" />
          </a>
          <a
            href="#programme"
            className="inline-flex items-center justify-center gap-2 border border-white/10 bg-white/[0.04] text-cream/80 px-10 py-4 rounded-full text-base font-medium hover:bg-white/[0.08] transition-all duration-300">
            
            En savoir plus
          </a>
        </div>

        {/* Link to main website */}
        <div className="mt-6 reveal-el revealed delay-5">
          <a
            href="https://gitezen.pro"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-cream/50 hover:text-moss-light transition-colors text-sm font-medium"
          >
            <Icon name="ArrowTopRightOnSquareIcon" size={16} />
            Découvrir GiteZen.pro
          </a>
        </div>

        {/* Trust row */}
        <div className="mt-12 flex flex-wrap items-center justify-center gap-6 text-xs text-cream/40 font-medium tracking-wide reveal-el revealed delay-5">
          <span className="flex items-center gap-1.5">
            <Icon name="CheckCircleIcon" size={14} className="text-moss-light" variant="solid" />
            Gratuit — 3 mois premium
          </span>
          <span className="flex items-center gap-1.5">
            <Icon name="CheckCircleIcon" size={14} className="text-moss-light" variant="solid" />
            Valeur estimée 500€+
          </span>
          <span className="flex items-center gap-1.5">
            <Icon name="CheckCircleIcon" size={14} className="text-moss-light" variant="solid" />
            Sélection trimestrielle
          </span>
          <span className="flex items-center gap-1.5">
            <Icon name="CheckCircleIcon" size={14} className="text-moss-light" variant="solid" />
            Sans engagement
          </span>
        </div>
      </div>
    </section>);

}